import { PHI_MAX, PHI_MIN, type CameraRig } from "../render/camera";
import { isCameraKey, keyAction, type KeyAction } from "./keys";

interface KeyboardDeps {
  helpOpen: () => boolean;
  materials: number;
  onAction: (action: KeyAction) => void;
}

export function initKeyboard(rig: CameraRig, { helpOpen, materials, onAction }: KeyboardDeps) {
  // Actions fire even from a focused slider (Space still drops); only the help blocks them
  addEventListener("keydown", (e) => {
    if (helpOpen()) return;
    const action = keyAction(e.code, e.key, materials);
    if (!action) return;
    if (action.type === "primary") e.preventDefault();
    onAction(action);
  });

  /* ---------- Keyboard camera: held keys move continuously ---------- */
  const held = new Set<string>();
  addEventListener("keydown", (e) => {
    if ((e.target as HTMLElement).tagName === "INPUT" || helpOpen()) return;
    if (isCameraKey(e.code)) {
      held.add(e.code);
      e.preventDefault();
    }
  });
  addEventListener("keyup", (e) => held.delete(e.code));
  addEventListener("blur", () => held.clear());

  const { camT, panBy, zoomAt } = rig;
  return {
    clearHeld: () => held.clear(),
    camera(dt: number) {
      const turn = 1.8 * dt,
        px = 500 * dt;
      if (held.has("ArrowLeft")) camT.theta -= turn;
      if (held.has("ArrowRight")) camT.theta += turn;
      if (held.has("ArrowUp")) camT.phi = Math.max(PHI_MIN, camT.phi - turn * 0.7);
      if (held.has("ArrowDown")) camT.phi = Math.min(PHI_MAX, camT.phi + turn * 0.7);
      if (held.has("KeyI")) panBy(0, px);
      if (held.has("KeyK")) panBy(0, -px);
      if (held.has("KeyJ")) panBy(px, 0);
      if (held.has("KeyL")) panBy(-px, 0);
      if (held.has("Equal") || held.has("NumpadAdd")) zoomAt(Math.exp(-1.5 * dt));
      if (held.has("Minus") || held.has("NumpadSubtract")) zoomAt(Math.exp(1.5 * dt));
    },
  };
}
